import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  User, 
  Lock, 
  Mail, 
  Eye, 
  EyeOff, 
  LogIn, 
  UserPlus,
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Upload,
  Download,
  RefreshCw,
  LogOut,
  Home,
  ChevronRight,
  Loader2,
  FileCheck,
  Edit,
  MapPin,
  Phone,
  Save,
  X,
  CalendarDays
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Header from "../components/Header";

const API = process.env.REACT_APP_BACKEND_URL;

// Status progress mapping
const STATUS_STEPS = [
  { key: "Intake Received", label: "Intake Received", description: "We have received your information" },
  { key: "In Preparation", label: "In Preparation", description: "Your tax return is being prepared" },
  { key: "Filed", label: "Filed with IRS", description: "Your return has been filed" },
  { key: "Completed", label: "Completed", description: "Process complete - refund issued" },
];

const getStatusProgress = (status) => {
  const index = STATUS_STEPS.findIndex(s => s.key === status);
  if (index === -1) return 0;
  return ((index + 1) / STATUS_STEPS.length) * 100;
};

// Auth helper
const getToken = () => localStorage.getItem("portal_token");
const setToken = (token) => localStorage.setItem("portal_token", token);
const clearToken = () => localStorage.removeItem("portal_token");
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

// Login Component
function PortalLogin({ onLogin }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tokenEmail, setTokenEmail] = useState("");
  const [verifyingToken, setVerifyingToken] = useState(false);

  // Check for reset token in URL on mount
  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      setVerifyingToken(true);
      setResetToken(token);
      // Verify the token is valid
      axios.get(`${API}/api/portal/verify-reset-token?token=${token}`)
        .then(response => {
          setTokenEmail(response.data.email || "");
          setMode("reset");
          setVerifyingToken(false);
        })
        .catch(() => {
          toast.error("This reset link is invalid or has expired. Please request a new one.");
          setMode("login");
          setVerifyingToken(false);
          navigate("/portal");
        });
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/api/portal/login" : "/api/portal/register";
      const response = await axios.post(`${API}${endpoint}`, { email, password });
      
      setToken(response.data.access_token);
      toast.success(mode === "login" ? "Welcome back!" : "Account created successfully!");
      onLogin(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail || "An error occurred";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/portal/forgot-password`, { email });
      toast.success("A password reset link has been sent to your email.");
      setMode("login");
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/portal/reset-password`, { 
        token: resetToken, 
        new_password: newPassword 
      });
      toast.success("Password reset successful! You can now log in.");
      setMode("login");
      setResetToken("");
      setNewPassword("");
      setConfirmPassword("");
      navigate("/portal");
    } catch (error) {
      const detail = error.response?.data?.detail || "Invalid or expired reset link";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  // Verifying token loading state
  if (verifyingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="border-0 shadow-xl w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-navy mx-auto mb-4" />
            <p className="text-slate-600">Verifying reset link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot Password View
  if (mode === "forgot") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 bg-navy rounded-full flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-gold" />
              </div>
              <CardTitle className="text-2xl font-heading text-navy">
                Forgot Password
              </CardTitle>
              <CardDescription>
                Enter your email and we'll send you a reset link
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="forgot-email"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-navy hover:bg-navy-light text-white"
                  disabled={loading}
                  data-testid="forgot-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>

              <div className="mt-6 text-center space-y-2">
                <button 
                  onClick={() => setMode("login")}
                  className="text-sm text-navy hover:underline"
                  data-testid="back-to-login"
                >
                  ← Back to Sign In
                </button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Reset Password View (when user clicks link from email)
  if (mode === "reset") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 bg-navy rounded-full flex items-center justify-center mb-4">
                <Lock className="w-8 h-8 text-gold" />
              </div>
              <CardTitle className="text-2xl font-heading text-navy">
                Create New Password
              </CardTitle>
              <CardDescription>
                {tokenEmail ? `Reset password for ${tokenEmail}` : "Enter your new password below"}
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 pr-10"
                      data-testid="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      data-testid="confirm-password"
                    />
                  </div>
                </div>

                <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                  Password must be at least 6 characters long.
                </p>

                <Button
                  type="submit"
                  className="w-full bg-navy hover:bg-navy-light text-white"
                  disabled={loading}
                  data-testid="reset-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Lock className="w-4 h-4 mr-2" />
                  )}
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button 
                  onClick={() => { setMode("login"); navigate("/portal"); }}
                  className="text-sm text-navy hover:underline"
                  data-testid="back-to-login-from-reset"
                >
                  ← Back to Sign In
                </button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Login / Register View
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-navy rounded-full flex items-center justify-center mb-4">
              <User className="w-8 h-8 text-gold" />
            </div>
            <CardTitle className="text-2xl font-heading text-navy">
              Client Portal
            </CardTitle>
            <CardDescription>
              {mode === "login" 
                ? "Sign in to view your tax service status"
                : "Create an account to track your services"
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <Tabs value={mode} onValueChange={setMode} className="mb-6">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="login" className="data-[state=active]:bg-navy data-[state=active]:text-white">
                  <LogIn className="w-4 h-4 mr-2" /> Sign In
                </TabsTrigger>
                <TabsTrigger value="register" className="data-[state=active]:bg-navy data-[state=active]:text-white">
                  <UserPlus className="w-4 h-4 mr-2" /> Register
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="portal-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-slate-700">Password</label>
                  {mode === "login" && (
                    <button 
                      type="button"
                      onClick={() => setMode("forgot")}
                      className="text-xs text-navy hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    data-testid="portal-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {mode === "register" && (
                <p className="text-xs text-slate-500 bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <strong>Note:</strong> You must have already submitted a client intake form with this email to create a portal account.
                </p>
              )}

              <Button
                type="submit"
                className="w-full bg-navy hover:bg-navy-light text-white"
                disabled={loading}
                data-testid="portal-submit"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : mode === "login" ? (
                  <LogIn className="w-4 h-4 mr-2" />
                ) : (
                  <UserPlus className="w-4 h-4 mr-2" />
                )}
                {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <a href="/" className="text-sm text-navy hover:underline inline-flex items-center">
                <Home className="w-4 h-4 mr-1" /> Back to Home
              </a>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

// Dashboard Component
function PortalDashboard({ user, onLogout }) {
  const [status, setStatus] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [missingDocs, setMissingDocs] = useState([]);
  const [archivedDocs, setArchivedDocs] = useState([]);
  const [reuploadMessage, setReuploadMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Reschedule appointment state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [rescheduling, setRescheduling] = useState(false);
  const [loadingTimes, setLoadingTimes] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, appointmentsRes, documentsRes, profileRes] = await Promise.all([
        axios.get(`${API}/api/portal/status`, getAuthHeaders()),
        axios.get(`${API}/api/portal/appointments`, getAuthHeaders()),
        axios.get(`${API}/api/portal/documents`, getAuthHeaders()),
        axios.get(`${API}/api/portal/me`, getAuthHeaders()),
      ]);

      setStatus(statusRes.data);
      setAppointments(appointmentsRes.data.appointments || []);
      setDocuments(documentsRes.data.documents || []);
      setMissingDocs(documentsRes.data.missing_docs || []);
      setArchivedDocs(documentsRes.data.archived_docs || []);
      setReuploadMessage(documentsRes.data.needs_reupload_message || null);
      setProfile(profileRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        onLogout();
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const startEditingProfile = () => {
    setProfileForm({
      first_name: profile?.first_name || "",
      last_name: profile?.last_name || "",
      phone: profile?.phone || "",
      address: profile?.address || "",
      city: profile?.city || "",
      state: profile?.state || "",
      zip_code: profile?.zip_code || "",
      filing_status: profile?.filing_status || "",
      occupation: profile?.occupation || "",
      spouse_first_name: profile?.spouse_first_name || "",
      spouse_last_name: profile?.spouse_last_name || "",
      spouse_phone: profile?.spouse_phone || "",
      spouse_email: profile?.spouse_email || "",
    });
    setEditingProfile(true);
  };

  const saveProfile = async () => {
    // Check if intake is locked
    if (status?.is_locked) {
      toast.error("Your intake is locked. Please contact us to make changes.");
      return;
    }
    
    setSavingProfile(true);
    try {
      // Update intake info instead of just profile
      await axios.patch(`${API}/api/portal/intake/update`, profileForm, getAuthHeaders());
      toast.success("Information updated successfully!");
      setEditingProfile(false);
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Your intake is locked because your return has been filed. Please contact us to make changes.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to update information");
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handleUpload = async (e, docType = "") => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", docType);

      await axios.post(`${API}/api/portal/documents/upload`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Document uploaded successfully!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleViewDocument = async (doc) => {
    if (!doc.is_available) {
      toast.error("This file is no longer available");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/api/portal/documents/${doc.id}/download`,
        { ...getAuthHeaders(), responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      window.open(url, '_blank');
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to view document");
    }
  };

  const handleDownload = async (doc) => {
    if (!doc.is_available) {
      toast.error("This file is no longer available");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/api/portal/documents/${doc.id}/download`,
        { ...getAuthHeaders(), responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.filename || "document");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download started!");
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const docTypeLabels = {
    state_id: "State ID",
    ssn_card: "SSN Card",
    dependent_ssn: "Dependent SSN Card",
    dependent_birth_cert: "Dependent Birth Certificate",
    w2_1099: "W-2 / 1099",
    deduction_receipts: "Deduction Receipts",
    school_form: "School Form",
    health_insurance: "Health Insurance",
  };

  // Reschedule functions
  const openRescheduleModal = async (appt) => {
    setRescheduleAppt(appt);
    setSelectedDate("");
    setSelectedTime("");
    setAvailableTimes([]);
    setLoadingTimes(false);
    
    try {
      // Fetch available dates
      const response = await axios.get(`${API}/api/appointments/available-dates`);
      setAvailableDates(response.data || []);
      setShowRescheduleModal(true);
    } catch (error) {
      toast.error("Failed to load available dates");
    }
  };

  const handleDateChange = (date) => {
    // Immediately update the selected date (sync operation)
    setSelectedDate(date);
    setSelectedTime("");
    setAvailableTimes([]);
    
    // Then fetch times asynchronously
    if (date) {
      setLoadingTimes(true);
      axios.get(`${API}/api/appointments/available-times/${date}`)
        .then(response => {
          setAvailableTimes(response.data || []);
        })
        .catch(() => {
          toast.error("Failed to load available times");
          setAvailableTimes([]);
        })
        .finally(() => {
          setLoadingTimes(false);
        });
    }
  };

  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime || !rescheduleAppt) {
      toast.error("Please select both date and time");
      return;
    }
    
    setRescheduling(true);
    try {
      await axios.post(
        `${API}/api/portal/appointments/${rescheduleAppt.id}/reschedule`,
        { 
          new_date: selectedDate,
          new_time: selectedTime 
        },
        getAuthHeaders()
      );
      
      toast.success("Appointment rescheduled successfully!");
      setShowRescheduleModal(false);
      setRescheduleAppt(null);
      fetchData(); // Refresh appointments
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reschedule appointment");
    } finally {
      setRescheduling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-navy animate-spin mx-auto mb-3" />
          <p className="text-slate-500">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="pt-[90px] md:pt-[98px] pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Welcome Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-navy">
                Welcome, {user?.name || "Client"}!
              </h1>
              <p className="text-slate-500 mt-1">Track your tax service status and documents</p>
            </div>
            <div className="flex gap-2 mt-4 md:mt-0">
              <Button
                onClick={fetchData}
                variant="outline"
                size="sm"
                className="border-navy text-navy"
                data-testid="portal-refresh"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
              <Button
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                data-testid="portal-logout"
              >
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>

          {/* Status Card */}
          {status?.has_intake ? (
            <Card className="mb-6 border-0 shadow-card" data-testid="portal-status-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading text-navy flex items-center gap-2">
                  <FileText className="w-5 h-5" /> Service Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex justify-between mb-2">
                    {STATUS_STEPS.map((step, i) => (
                      <div
                        key={step.key}
                        className={`text-xs text-center flex-1 ${
                          STATUS_STEPS.findIndex(s => s.key === status.status) >= i
                            ? "text-navy font-medium"
                            : "text-slate-400"
                        }`}
                      >
                        {step.label}
                      </div>
                    ))}
                  </div>
                  <Progress value={getStatusProgress(status.status)} className="h-2" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Current Status</p>
                    <p className="font-semibold text-navy flex items-center gap-1 mt-1">
                      {status.status === "Completed" ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <Clock className="w-4 h-4 text-amber-500" />
                      )}
                      {status.status}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Services</p>
                    <p className="font-semibold text-navy mt-1 text-sm">
                      {status.services?.map(s => s.replace(/_/g, " ")).join(", ") || "-"}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Filing Status</p>
                    <p className="font-semibold text-navy mt-1 capitalize">
                      {status.filing_status?.replace(/_/g, " ") || "-"}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Refund Status</p>
                    <p className="font-semibold text-navy flex items-center gap-1 mt-1">
                      {status.refund_status === "Received" ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <Clock className="w-4 h-4 text-amber-500" />
                      )}
                      {status.refund_status}
                    </p>
                  </div>
                </div>

                {status.fee && (
                  <div className="mt-4 p-3 bg-gold/10 border border-gold/30 rounded-lg">
                    <p className="text-sm text-navy">
                      <strong>Service Fee:</strong> ${status.fee.toFixed(2)}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="mb-6 border-0 shadow-card bg-amber-50 border-amber-200">
              <CardContent className="p-6 text-center">
                <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-navy mb-2">No Intake Found</h3>
                <p className="text-slate-600 mb-4">
                  You haven't submitted a client intake form yet.
                </p>
                <Button asChild className="bg-navy hover:bg-navy-light">
                  <a href="/intake">
                    <FileText className="w-4 h-4 mr-2" /> Start Intake Form
                  </a>
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {/* Appointments */}
            <Card className="border-0 shadow-card" data-testid="portal-appointments">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading text-navy flex items-center gap-2">
                  <Calendar className="w-5 h-5" /> Appointments
                </CardTitle>
              </CardHeader>
              <CardContent>
                {appointments.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-4">No appointments scheduled</p>
                ) : (
                  <ul className="space-y-3">
                    {appointments.map((appt) => (
                      <li
                        key={appt.id}
                        className="p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-medium text-navy text-sm">
                              {formatDateTime(appt.start_time)}
                            </p>
                            <p className="text-xs text-slate-500">30-minute consultation</p>
                          </div>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              appt.status === "Scheduled"
                                ? "bg-blue-100 text-blue-700"
                                : appt.status === "Cancelled"
                                ? "bg-red-100 text-red-700"
                                : "bg-slate-100 text-slate-700"
                            }`}
                          >
                            {appt.status}
                          </span>
                        </div>
                        {appt.status === "Scheduled" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full mt-2 text-xs border-navy text-navy hover:bg-navy hover:text-white"
                            onClick={() => openRescheduleModal(appt)}
                            data-testid={`reschedule-btn-${appt.id}`}
                          >
                            <CalendarDays className="w-3 h-3 mr-1" /> Reschedule Appointment
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Documents */}
            <Card className="border-0 shadow-card" data-testid="portal-documents">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading text-navy flex items-center gap-2">
                  <FileCheck className="w-5 h-5" /> Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Missing Documents Alert */}
                {missingDocs.length > 0 && (
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-medium text-amber-800 mb-2">
                      Missing Documents:
                    </p>
                    <ul className="space-y-2">
                      {missingDocs.map((docType) => (
                        <li key={docType} className="flex items-center justify-between">
                          <span className="text-sm text-amber-700">
                            {docTypeLabels[docType] || docType}
                          </span>
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => handleUpload(e, docType)}
                              disabled={uploading}
                            />
                            <span className="text-xs px-2 py-1 bg-navy text-white rounded hover:bg-navy-light inline-flex items-center gap-1">
                              <Upload className="w-3 h-3" /> Upload
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Uploaded Documents */}
                {documents.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-4">No documents uploaded</p>
                ) : (
                  <ul className="space-y-2">
                    {documents.map((doc, idx) => (
                      <li
                        key={`${doc.id}-${idx}`}
                        className={`flex items-center justify-between p-2 rounded-lg border ${
                          doc.is_available ? "bg-white" : "bg-amber-50 border-amber-200"
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm text-navy truncate">
                            {docTypeLabels[doc.doc_type] || doc.doc_type}: {doc.filename}
                          </p>
                          <p className="text-xs text-slate-400">
                            {doc.content_type} • {doc.size ? `${Math.round(doc.size / 1024)}KB` : ""}
                          </p>
                          {!doc.is_available && (
                            <p className="text-xs text-amber-600 mt-1">
                              File unavailable - please re-upload
                            </p>
                          )}
                        </div>
                        {doc.is_available ? (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleViewDocument(doc)}
                              className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 inline-flex items-center gap-1"
                              data-testid={`view-${doc.id}`}
                            >
                              <Eye className="w-3 h-3" /> View
                            </button>
                            <button
                              onClick={() => handleDownload(doc)}
                              className="text-xs px-2 py-1 bg-navy text-white rounded hover:bg-navy-light inline-flex items-center gap-1"
                              data-testid={`download-${doc.id}`}
                            >
                              <Download className="w-3 h-3" /> Download
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs px-2 py-1 bg-slate-200 text-slate-500 rounded">
                            Unavailable
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {/* General Upload */}
                <div className="mt-4 pt-4 border-t">
                  <label className="cursor-pointer block">
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => handleUpload(e)}
                      disabled={uploading}
                    />
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-navy hover:bg-slate-50 transition-colors">
                      {uploading ? (
                        <Loader2 className="w-6 h-6 text-navy mx-auto animate-spin" />
                      ) : (
                        <Upload className="w-6 h-6 text-slate-400 mx-auto" />
                      )}
                      <p className="text-sm text-slate-500 mt-2">
                        {uploading ? "Uploading..." : "Click to upload additional document"}
                      </p>
                    </div>
                  </label>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Update My Information Card */}
          <Card className="mt-6 border-0 shadow-card" data-testid="portal-profile">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-heading text-navy flex items-center gap-2">
                  <User className="w-5 h-5" /> Update My Information
                </CardTitle>
                {status?.is_locked ? (
                  <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-1 rounded-full">
                    <Lock className="w-4 h-4" /> Locked (Filed)
                  </div>
                ) : !editingProfile ? (
                  <Button
                    onClick={startEditingProfile}
                    variant="outline"
                    size="sm"
                    className="text-navy border-navy hover:bg-navy hover:text-white"
                    data-testid="edit-profile-btn"
                  >
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setEditingProfile(false)}
                      variant="outline"
                      size="sm"
                    >
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                    <Button
                      onClick={saveProfile}
                      disabled={savingProfile}
                      size="sm"
                      className="bg-navy hover:bg-navy-light text-white"
                    >
                      {savingProfile ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                      Save
                    </Button>
                  </div>
                )}
              </div>
              {status?.is_locked && (
                <p className="text-sm text-amber-700 mt-2 bg-amber-50 p-2 rounded">
                  ⚠️ Your intake is locked because your return has been filed or is being processed. 
                  Please contact us if you need to make changes.
                </p>
              )}
            </CardHeader>
            <CardContent>
              {profile ? (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-500">Full Name</label>
                      {editingProfile ? (
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          <Input
                            value={profileForm.first_name}
                            onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                            placeholder="First Name"
                          />
                          <Input
                            value={profileForm.last_name}
                            onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })}
                            placeholder="Last Name"
                          />
                        </div>
                      ) : (
                        <p className="font-medium text-navy">
                          {profile.first_name} {profile.last_name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Email</label>
                      <p className="font-medium text-navy flex items-center gap-1">
                        <Mail className="w-4 h-4 text-slate-400" /> {profile.email || "-"}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Email cannot be changed here</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Phone</label>
                      {editingProfile ? (
                        <Input
                          value={profileForm.phone}
                          onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                          className="mt-1"
                          placeholder="Phone number"
                        />
                      ) : (
                        <p className="font-medium text-navy flex items-center gap-1">
                          <Phone className="w-4 h-4 text-slate-400" /> {profile.phone || "-"}
                        </p>
                      )}
                    </div>
                    {profile.date_of_birth && (
                      <div>
                        <label className="text-xs text-slate-500">Date of Birth</label>
                        <p className="font-medium text-navy">{profile.date_of_birth}</p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-500">Address</label>
                      {editingProfile ? (
                        <Input
                          value={profileForm.address}
                          onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                          className="mt-1"
                          placeholder="Street address"
                        />
                      ) : (
                        <p className="font-medium text-navy flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-slate-400" /> {profile.address || "-"}
                        </p>
                      )}
                    </div>
                    {editingProfile ? (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-slate-500">City</label>
                            <Input
                              value={profileForm.city}
                              onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                              className="mt-1"
                              placeholder="City"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">State</label>
                            <Input
                              value={profileForm.state}
                              onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
                              className="mt-1"
                              placeholder="State"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-500">Zip Code</label>
                          <Input
                            value={profileForm.zip_code}
                            onChange={(e) => setProfileForm({ ...profileForm, zip_code: e.target.value })}
                            className="mt-1"
                            placeholder="Zip code"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <label className="text-xs text-slate-500">City, State, Zip</label>
                          <p className="font-medium text-navy">
                            {profile.city || "-"}, {profile.state || "-"} {profile.zip_code || ""}
                          </p>
                        </div>
                      </>
                    )}
                    {profile.filing_status && (
                      <div>
                        <label className="text-xs text-slate-500">Filing Status</label>
                        <p className="font-medium text-navy capitalize">
                          {profile.filing_status?.replace(/_/g, " ")}
                        </p>
                      </div>
                    )}
                    {profile.spouse_first_name && (
                      <div>
                        <label className="text-xs text-slate-500">Spouse</label>
                        <p className="font-medium text-navy">
                          {profile.spouse_first_name} {profile.spouse_last_name}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-sm text-center py-4">Loading profile...</p>
              )}
            </CardContent>
          </Card>

          {/* Contact Card */}
          <Card className="mt-6 border-0 shadow-card bg-navy text-white">
            <CardContent className="p-6 text-center">
              <h3 className="text-lg font-heading mb-2">Need Help?</h3>
              <p className="text-slate-300 mb-4">
                Contact us if you have any questions about your tax services.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="tel:9014013054"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gold text-navy rounded-lg font-medium hover:bg-gold-light transition-colors"
                >
                  Call (901) 401-3054
                </a>
                <a
                  href="mailto:info@wmjenterpriseservices.com"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
                >
                  Email Us
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Reschedule Appointment Modal */}
      <Dialog open={showRescheduleModal} onOpenChange={setShowRescheduleModal}>
        <DialogContent className="max-w-md" data-testid="reschedule-modal">
          <DialogHeader>
            <DialogTitle className="text-navy flex items-center gap-2">
              <CalendarDays className="w-5 h-5" /> Reschedule Appointment
            </DialogTitle>
            <DialogDescription>
              Select a new date and time for your consultation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Appointment */}
            {rescheduleAppt && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Current Appointment:</p>
                <p className="font-medium text-navy text-sm">
                  {formatDateTime(rescheduleAppt.start_time)}
                </p>
              </div>
            )}
            
            {/* Date Selection */}
            <div>
              <label className="text-sm font-medium text-navy block mb-2">
                Select New Date
              </label>
              <Select value={selectedDate} onValueChange={handleDateChange}>
                <SelectTrigger data-testid="reschedule-date-select">
                  <SelectValue placeholder="Choose a date..." />
                </SelectTrigger>
                <SelectContent>
                  {availableDates.map((date) => (
                    <SelectItem key={date} value={date}>
                      {new Date(date + "T00:00:00").toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric"
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Time Selection */}
            {selectedDate && (
              <div>
                <label className="text-sm font-medium text-navy block mb-2">
                  Select New Time
                </label>
                {loadingTimes ? (
                  <div className="flex items-center justify-center py-3 bg-slate-50 rounded-lg">
                    <Loader2 className="w-5 h-5 text-navy animate-spin mr-2" />
                    <span className="text-sm text-slate-500">Loading times...</span>
                  </div>
                ) : availableTimes.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-3 bg-slate-50 rounded-lg">
                    No available times for this date
                  </p>
                ) : (
                  <Select value={selectedTime} onValueChange={setSelectedTime}>
                    <SelectTrigger data-testid="reschedule-time-select">
                      <SelectValue placeholder="Choose a time..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTimes.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
          
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowRescheduleModal(false)}
              disabled={rescheduling}
            >
              Cancel
            </Button>
            <Button
              className="bg-navy hover:bg-navy-light"
              onClick={handleReschedule}
              disabled={!selectedDate || !selectedTime || rescheduling}
              data-testid="confirm-reschedule-btn"
            >
              {rescheduling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rescheduling...
                </>
              ) : (
                "Confirm Reschedule"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main Portal Component
export default function ClientPortal() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getToken();
    if (token) {
      // Verify token by calling /me endpoint
      axios
        .get(`${API}/api/portal/me`, getAuthHeaders())
        .then((res) => {
          setUser(res.data);
          setIsAuthenticated(true);
        })
        .catch(() => {
          clearToken();
        });
    }
  }, []);

  const handleLogin = (data) => {
    setUser({ name: data.name, id: data.client_id });
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    clearToken();
    setUser(null);
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return <PortalLogin onLogin={handleLogin} />;
  }

  return <PortalDashboard user={user} onLogout={handleLogout} />;
}
